import type { MerchantItemIdentity } from '../../../app/Libraries/MerchantTracking';

/** Native bag state: real Shopify responses remain authoritative. No DOM, analytics or network client is created here. */
export type CartIssue = '' | 'load' | 'quantity' | 'update' | 'remove' | 'checkout' | 'availability';
export function cartQuantity(value: unknown): number | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  if (!/^[1-9][0-9]*$/.test(text)) return null;
  const quantity = Number(text);
  return Number.isSafeInteger(quantity) ? quantity : null;
}
export function cartHasUnavailableItems(cart: any): boolean {
  return !!cart?.lineItems?.some((line: any) => !line.variant?.id || line.variant.availableForSale === false);
}
export function cartCheckoutUrl(cart: any): string | null {
  if (typeof cart?.webUrl !== 'string') return null;
  try { const url = new URL(cart.webUrl); return url.protocol === 'https:' && !url.username && !url.password ? cart.webUrl : null; }
  catch { return null; }
}
export function createCartSession(client: any, changed: (state: any) => void, availability?: { check: (cart: any) => Promise<any>; result: (result: any) => void }) {
  let cart: any = null;
  let pending = '';
  let issue: CartIssue = '';
  let requestedId: string | null = null;
  const valid = (value: any) => !!value?.id && Array.isArray(value.lineItems);
  const snapshot = () => ({ cart, pending, issue, unavailable: cartHasUnavailableItems(cart), phase: pending === 'load' ? 'loading' : !cart && issue ? 'error' : cart?.lineItems.length ? 'ready' : 'empty' });
  const notify = () => changed(snapshot());
  async function validateAvailability() {
    if (!availability || !cart) return null;
    const result = await availability.check(cart);
    if (valid(result.cart)) cart = result.cart;
    availability.result(result);
    if (result.status === 'remove-failed') issue = 'availability';
    return result;
  }
  async function load(id: string | null = requestedId) {
    if (pending) return false;
    requestedId = id; pending = 'load'; issue = ''; notify();
    try {
      const value = id ? await client.cart.fetch(id) : null;
      if (value != null && !valid(value)) throw new Error('Cart response invalid');
      cart = value || null;
      const checked = await validateAvailability();
      return checked?.status !== 'remove-failed';
    }
    catch { issue = 'load'; return false; }
    finally { pending = ''; notify(); }
  }
  async function change(lineId: string, quantity: unknown, remove = false) {
    if (pending || !cart) return false;
    if (!cart.lineItems.some((item: any) => item.id === lineId)) return false;
    const amount = remove ? 0 : cartQuantity(quantity);
    if (amount === null) { issue = 'quantity'; notify(); return false; }
    pending = remove ? 'remove' : 'update'; issue = ''; notify();
    try {
      const value = remove ? await client.cart.removeLineItems(cart.id, lineId) : await client.cart.updateLineItems(cart.id, [{ id: lineId, quantity: amount }]);
      if (!valid(value)) throw new Error('Cart unavailable');
      cart = value;
      const confirmed = cart.lineItems.find((item: any) => item.id === lineId);
      if (remove ? !!confirmed : !confirmed || Number(confirmed.quantity) !== amount) throw new Error('Cart did not confirm requested change');
      return true;
    } catch { issue = remove ? 'remove' : 'update'; return false; }
    finally { pending = ''; notify(); }
  }
  async function checkout(note: string) {
    if (pending || !cart?.lineItems.length) return null;
    if (cartHasUnavailableItems(cart)) { issue = 'availability'; notify(); return null; }
    pending = 'checkout'; issue = ''; notify();
    try {
      const latest = await client.cart.fetch(cart.id);
      if (latest == null) { cart = null; return null; }
      if (!valid(latest)) throw new Error('Cart response invalid');
      cart = latest;
      const checked = await validateAvailability();
      // A changed bag is shown for review; this click never redirects after removal.
      if (checked?.removed || checked?.status === 'remove-failed') return null;
      if (!cart.lineItems.length) return null;
      if (cartHasUnavailableItems(cart)) { issue = 'availability'; return null; }
      const checkedLines = JSON.stringify(cart.lineItems.map((line: any) => [line.id, line.variant?.id, line.quantity, line.customAttributes || []]));
      const updated = await client.cart.updateAttributes(cart.id, { note });
      if (updated === null || updated === false || updated?.userErrors?.length || updated?.errors?.length) throw new Error('Cart note was not confirmed');
      // Wrappers may return a Cart, a mutation envelope, or no value. Confirm
      // the authoritative cart without depending on an undocumented return shape.
      const confirmed = valid(updated) ? updated : valid(updated?.cart) ? updated.cart : await client.cart.fetch(cart.id);
      if (!valid(confirmed)) throw new Error('Cart unavailable after note update');
      if (Object.prototype.hasOwnProperty.call(confirmed, 'note') && confirmed.note !== undefined && (confirmed.note || '') !== note) throw new Error('Cart note was not saved');
      cart = confirmed;
      if (availability && JSON.stringify(cart.lineItems.map((line: any) => [line.id, line.variant?.id, line.quantity, line.customAttributes || []])) !== checkedLines) {
        const rechecked = await validateAvailability();
        if (rechecked?.removed || rechecked?.status === 'remove-failed') return null;
      }
      if (!cart.lineItems.length) return null;
      if (cartHasUnavailableItems(cart)) { issue = 'availability'; return null; }
      const url = cartCheckoutUrl(cart);
      if (!url) throw new Error('Checkout unavailable');
      return url;
    } catch { issue = 'checkout'; return null; }
    finally { pending = ''; notify(); }
  }
  return { snapshot, load, update: (lineId: string, quantity: unknown) => change(lineId, quantity), remove: (lineId: string) => change(lineId, 0, true), checkout };
}

/** Emit only the quantity actually confirmed by Shopify, with its real currency. */
export function cartQuantityChange(before: any, after: any, lineId: string, identity?: MerchantItemIdentity) {
  const previous = before?.lineItems?.find((line: any) => line.id === lineId);
  const current = after?.lineItems?.find((line: any) => line.id === lineId);
  const delta = Number(current?.quantity || 0) - Number(previous?.quantity || 0);
  const line = current || previous;
  const price = Number(line?.variant?.price?.amount);
  if (!line || !Number.isFinite(delta) || !delta || !Number.isFinite(price)) return null;
  const quantity = Math.abs(delta), currency = after?.currencyCode || before?.currencyCode;
  return { name: delta > 0 ? 'add_to_cart' : 'remove_from_cart', payload: { currency, value: price * quantity, items: [{ ...(identity || { item_id: line.variant.sku }), item_name: line.title, item_variant: line.variant.title, price, quantity, currency }] } };
}
